import React from "react";

import { cn } from "@/lib/utils";
import RouteTabs, { RouteTabItem } from "../../Core/RouteTabs";
import ListActionsMenu, { ListToolbarAction } from "../ListPageToolbar/ListActionsMenu";

export type { RouteTabItem } from "../../Core/RouteTabs";
export type { ListToolbarAction } from "../ListPageToolbar/ListActionsMenu";

export interface DetailPageTabsProps {
	/** The tabs to render — same shape as {@link RouteTabs}. */
	tabs: RouteTabItem[];
	/**
	 * Overflow actions shown in the "..." menu at the right edge of the bar
	 * (the same menu used by the list-page toolbar). Omitted/empty → no menu.
	 */
	actions?: ListToolbarAction[];
	/**
	 * Extra DropdownMenu content for the "..." menu (e.g. a tags submenu),
	 * rendered between the regular actions and the first `dividerBefore`
	 * action — see {@link ListActionsMenu}.
	 */
	actionsExtraItems?: React.ReactNode;
	/**
	 * Custom right-edge content. Overrides the `actions` menu when provided —
	 * use it when a page needs something other than the standard overflow menu.
	 */
	rightSlot?: React.ReactNode;
	/** Called right before navigating when a tab is selected. */
	onBeforeNavigate?: () => void;
	/** Skip i18n translation of the tab labels. */
	skipTranslation?: boolean;
	/** Outer container — merged over the default top margin. */
	className?: string;
	/** The boxed tab bar — merged over the default border/background. */
	barClassName?: string;
	/** Each trigger — merged over the default rounded active-box styling. */
	triggerClassName?: string;
	/** The content panel — merged over the default top margin. */
	contentClassName?: string;
}

/**
 * The standard detail-page tab bar — a white, rounded, bordered bar of
 * route-driven tabs with a rounded highlight on the active tab and an optional
 * "..." overflow menu, sitting above the active tab's content.
 *
 * It is the shared, "styled for this purpose" preset of the generic
 * {@link RouteTabs} primitive, used across the customer / consultant / job
 * detail pages so they don't each re-spell the same Tailwind classes. Pass
 * `actions` to get the overflow menu; every visual is overridable via the
 * `*ClassName` props if a page needs to deviate.
 *
 * @example
 * <DetailPageTabs
 *   tabs={[
 *     { route: routes.CUSTOMER_PROFILE, label: "PROFILE", content: <CustomerProfileTab /> },
 *     { route: routes.CUSTOMER_JOBS, label: "JOBS", content: <CustomerJobsTab customer={customer} /> },
 *   ]}
 *   actions={customerActions}
 * />
 */
const DetailPageTabs: React.FC<DetailPageTabsProps> = ({
	tabs,
	actions,
	actionsExtraItems,
	rightSlot,
	onBeforeNavigate,
	skipTranslation,
	className,
	barClassName,
	triggerClassName,
	contentClassName,
}) => {
	const right =
		rightSlot ??
		(actions && actions.length > 0 ? (
			<ListActionsMenu actions={actions} extraItems={actionsExtraItems} />
		) : undefined);

	return (
		<RouteTabs
			tabs={tabs}
			rightSlot={right}
			onBeforeNavigate={onBeforeNavigate}
			skipTranslation={skipTranslation}
			className={cn("mt-4", className)}
			barClassName={cn(
				"sticky top-2 z-20 rounded-lg border border-gray-200 bg-white px-2 py-1.5",
				barClassName
			)}
			triggerClassName={cn(
				"rounded-md border py-2 data-[state=active]:border-action/30 data-[state=active]:bg-action/10",
				triggerClassName
			)}
			contentClassName={cn("mt-2", contentClassName)}
		/>
	);
};

export default DetailPageTabs;
